<template>
  <div :class="['recipe-grid', { 'cooking-mode': cookingMode }]">
    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>Loading recipe...</p>
    </div>

    <template v-else-if="recipe">
      <section class="header">
        <h1>
          {{ recipe.name }}
          <span v-if="recipe.category" class="category-badge">{{ recipe.category }}</span>
          <small v-if="recipe.url != ''" class="source-link">(<a :href="recipe.url">via</a>)</small>
        </h1>
        <p class="description">{{ recipe.description }}</p>
        
        <!-- Controls bar -->
        <div class="recipe-controls">
          <button 
            @click="toggleCookingMode" 
            :class="['control-btn', 'start-btn', { active: cookingMode }]"
            :title="cookingMode ? 'Exit cooking mode' : 'Enter cooking mode'"
          >
            {{ cookingMode ? 'Exit Cooking Mode' : 'Start Cooking' }}
          </button>
          <router-link 
            v-if="isAdmin" 
            :to="'/recipe/' + id + '/edit'" 
            class="control-btn edit-btn"
          >
            Edit Recipe
          </router-link>
        </div>
      </section>
      <section class="meta"></section>

      <section class="ingredients">
        <h2>Ingredients</h2>
        <ul>
          <div v-for="(i, index) in recipe.ingredients" :key="index">
            <li v-if="i.indexOf('###') != -1" class="ingredient-group">
              <h3>{{ i.replace("###", "") }}</h3>
            </li>
            <li v-else class="ingredient">
              <label class="ingredient-checkbox">
                <input type="checkbox" v-model="checkedIngredients[index]" />
                <span :class="{ 'checked': checkedIngredients[index] }">{{ i }}</span>
              </label>
            </li>
          </div>
        </ul>
      </section>

      <section class="instructions">
        <h2>Instructions</h2>
        <h5 v-if="recipe.persons" class="persons-info">
          {{ recipe.persons }}
        </h5>
        <div v-for="(section, sectionIndex) in structuredInstructions" :key="sectionIndex">
          <h4 v-if="section.heading">{{ section.heading }}</h4>
          <ol>
            <li 
              v-for="(instruction, instructionIndex) in section.instructions" 
              :key="instructionIndex"
              :class="['instruction', { 'current-step': cookingMode && currentStep === getGlobalStepIndex(sectionIndex, instructionIndex) }]"
              @click="cookingMode && setCurrentStep(getGlobalStepIndex(sectionIndex, instructionIndex))"
            >
              {{ instruction }}
            </li>
          </ol>
        </div>
        
        <!-- Cooking mode navigation -->
        <div v-if="cookingMode" class="cooking-nav">
          <button @click="prevStep" :disabled="currentStep <= 0" class="nav-btn">← Previous</button>
          <span class="step-counter">Step {{ currentStep + 1 }} of {{ totalSteps }}</span>
          <button @click="nextStep" :disabled="currentStep >= totalSteps - 1" class="nav-btn">Next →</button>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
export default {
  name: "DetailsView",
  props: {
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      id: "",
      recipe: null,
      loading: true,
      checkedIngredients: {},
      cookingMode: false,
      currentStep: 0,
      wakeLock: null,
    };
  },
  created() {
    this.id = this.$route.params.id;
    this.getData(this.id);
  },
  beforeDestroy() {
    this.releaseWakeLock();
  },

  methods: {
    async getData(id) {
      this.loading = true;
      try {
        let response = await this.$http.get("/recipe/findById?id=" + id);
        this.recipe = response.data;
      } catch (e) {
        console.error("Failed to load recipe", e);
      } finally {
        this.loading = false;
      }
    },
    
    toggleCookingMode() {
      this.cookingMode = !this.cookingMode;
      if (this.cookingMode) {
        this.currentStep = 0;
        this.requestWakeLock();
        // Scroll to first step on mobile
        this.$nextTick(() => this.scrollToCurrentStep());
      } else {
        this.releaseWakeLock();
      }
    },
    
    async requestWakeLock() {
      if ('wakeLock' in navigator) {
        try {
          this.wakeLock = await navigator.wakeLock.request('screen');
        } catch (e) {
          console.log('Wake lock not available');
        }
      }
    },
    
    releaseWakeLock() {
      if (this.wakeLock) {
        this.wakeLock.release();
        this.wakeLock = null;
      }
    },
    
    getGlobalStepIndex(sectionIndex, instructionIndex) {
      let index = 0;
      for (let i = 0; i < sectionIndex; i++) {
        index += this.structuredInstructions[i].instructions.length;
      }
      return index + instructionIndex;
    },
    
    setCurrentStep(index) {
      this.currentStep = index;
      this.$nextTick(() => this.scrollToCurrentStep());
    },
    
    nextStep() {
      if (this.currentStep < this.totalSteps - 1) {
        this.currentStep++;
        this.$nextTick(() => this.scrollToCurrentStep());
      }
    },
    
    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
        this.$nextTick(() => this.scrollToCurrentStep());
      }
    },
    
    scrollToCurrentStep() {
      const currentStepEl = this.$el.querySelector('.instruction.current-step');
      if (currentStepEl) {
        currentStepEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
  },
  computed: {
    structuredInstructions() {
      if (!this.recipe?.instructions) return [];
      
      const sections = [];
      let currentSection = { heading: null, instructions: [] };

      this.recipe.instructions.forEach(item => {
        if (item.startsWith('###')) {
          if (currentSection.instructions.length > 0) {
            sections.push(currentSection);
          }
          currentSection = { heading: item.replace('###', '').trim(), instructions: [] };
        } else {
          currentSection.instructions.push(item);
        }
      });

      if (currentSection.instructions.length > 0) {
        sections.push(currentSection);
      }

      return sections;
    },
    
    totalSteps() {
      return this.structuredInstructions.reduce((sum, section) => sum + section.instructions.length, 0);
    },
  },
};
</script>

<style scoped>
@import "../styles/base.css";
@import "../styles/recipe.css";

/* Loading state */
.loading-state {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4em 2em;
  color: var(--text-muted, #666);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color, #ddd);
  border-top-color: var(--accent-color, #0071e3);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Recipe controls */
.recipe-controls {
  display: flex;
  gap: 0.5em;
  margin-top: 1em;
}

.control-btn {
  padding: 0.6em 1.2em;
  background: var(--input-bg, #f2f2f2);
  border: 1px solid var(--border-color, #ddd);
  border-radius: 0.5em;
  cursor: pointer;
  font-size: 0.95em;
  color: var(--text-color);
  transition: all 0.2s;
}

.control-btn:hover {
  background: var(--accent-color);
  color: white;
  border-color: var(--accent-color);
}

.control-btn.active {
  background: var(--accent-color);
  color: white;
  border-color: var(--accent-color);
}

/* Primary call-to-action: Start Cooking */
.start-btn {
  background: var(--accent-color);
  border-color: var(--accent-color);
  color: #fff;
}

.start-btn:hover {
  background: var(--accent-hover);
  border-color: var(--accent-hover);
  color: #fff;
}

.edit-btn {
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}

/* Cooking mode */
.cooking-mode {
  font-size: 1.15em;
}

.cooking-mode .instruction {
  padding: 0.75em;
  border-radius: 0.5em;
  cursor: pointer;
  transition: background 0.2s;
}

.cooking-mode .instruction:hover {
  background: var(--input-bg, #f2f2f2);
}

.cooking-mode .instruction.current-step {
  background: var(--accent-color);
  color: white;
  font-weight: 500;
}

.cooking-nav {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.5em;
  margin-top: 2em;
  padding: 1em;
  background: var(--card-bg, #fff);
  border: 1px solid var(--border-color, #ddd);
  border-radius: 0.75em;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.12);
  position: sticky;
  bottom: 1em;
}

.nav-btn {
  padding: 0.75em 1.5em;
  background: var(--accent-color);
  color: white;
  border: none;
  border-radius: 0.5em;
  cursor: pointer;
  font-size: 1em;
  transition: opacity 0.2s;
}

.nav-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.step-counter {
  font-weight: 500;
  color: var(--text-muted, #666);
}

.persons-info {
  color: var(--text-muted, #666);
  font-weight: normal;
}
</style>
